import { Fragment, PropsWithChildren, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuthStore } from "../store/auth.store";
import { http } from "../api/http";
import { isTodayKey } from "../views/today/todayKeys";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function AppProviders({ children }: PropsWithChildren) {
  useEffect(() => {
    let lastRefresh = 0;
    const refresh = () => {
      lastRefresh = Date.now();
      void queryClient.invalidateQueries({
        predicate: (q) => isTodayKey(q.queryKey),
      });
    };
    const resume = () => {
      if (Date.now() - lastRefresh >= 30000) refresh();
    };
    const written = (method?: string, url = "") =>
      method &&
      !["get", "head", "options"].includes(method.toLowerCase()) &&
      // Le signal de présence n'écrit rien que l'écran « Aujourd'hui » affiche.
      !/\/auth\/|\/references\/resolve(?:\?|$)|\/preview(?:\?|$)|\/presence\/heartbeat(?:\?|$)/.test(url);
    const interceptor = http.interceptors.response.use(
      (response) => {
        if (written(response.config.method, response.config.url)) refresh();
        return response;
      },
      (error) => {
        if (error.response?.status !== 429 && written(error.config?.method, error.config?.url)) refresh();
        return Promise.reject(error);
      },
    );
    window.addEventListener("focus", resume);
    window.addEventListener("online", resume);
    return () => {
      http.interceptors.response.eject(interceptor);
      window.removeEventListener("focus", resume);
      window.removeEventListener("online", resume);
    };
  }, []);
  const scope = useAuthStore((state) =>
    [state.user?.id, state.user?.role, state.activeSchoolId].join(":"),
  );
  const bootstrap = useAuthStore((state) => state.bootstrap);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  useEffect(
    () =>
      useAuthStore.subscribe((state, previous) => {
        if (
          state.user?.id !== previous.user?.id ||
          state.activeSchoolId !== previous.activeSchoolId ||
          state.user?.role !== previous.user?.role
        )
          queryClient.clear();
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <Fragment key={scope}>{children}</Fragment>
    </QueryClientProvider>
  );
}
